import React from 'react';
import { ShieldCheck, LineChart, Briefcase, ArrowDownCircle, FileText } from 'lucide-react';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description }) => {
  return (
    <div className="bg-navy-800 p-6 rounded-xl border border-gray-700 transition-all hover:border-gold-500/50 hover:shadow-lg hover:shadow-gold-500/10">
      <div className="flex items-start">
        <div className="mr-4 bg-navy-700 p-3 rounded-lg text-gold-500">
          {icon}
        </div>
        <div>
          <h3 className="font-semibold text-lg text-white mb-2">{title}</h3>
          <p className="text-gray-300">{description}</p>
        </div>
      </div>
    </div>
  );
};

const WhySection: React.FC = () => {
  return (
    <section id="why" className="py-20 bg-navy-900">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why AAbA Stable?</h2>
          <div className="w-20 h-1 bg-gold-500 mx-auto rounded-full mb-6"></div>
          <p className="text-gray-300 max-w-2xl mx-auto text-lg">
            Our premium solution combines security, predictability, and expertise to deliver steady returns without the typical DeFi headaches.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <FeatureCard
            icon={<ShieldCheck size={24} />}
            title="Full 1:1 Reserve"
            description="Every dollar you deposit is backed by highly liquid USD-stablecoins—capital security you can verify."
          />
          <FeatureCard
            icon={<LineChart size={24} />}
            title="Quarterly Income"
            description="Receive payouts every three months for clear, predictable cash-flow planning."
          />
          <FeatureCard
            icon={<Briefcase size={24} />}
            title="Institutional-Grade DeFi Strategies"
            description="We hand-select low-risk protocols and automate staking, farming, and rebalancing on your behalf."
          />
          <FeatureCard
            icon={<ArrowDownCircle size={24} />}
            title="Flexible Withdrawals"
            description="Cash pickup in Dubai, SEPA transfer to Europe, or a custom arrangement—we handle the logistics so you don't have to."
          />
          <FeatureCard
            icon={<FileText size={24} />}
            title="Transparent Reporting"
            description="Detailed performance reports delivered in a format familiar to professional investors."
          />
          <div className="bg-gradient-to-br from-gold-500 to-amber-600 p-6 rounded-xl text-navy-900 flex flex-col justify-center">
            <h3 className="font-bold text-xl mb-3">Ready to get started?</h3>
            <p className="mb-4">Join investors already earning steady returns with AAbA Stable.</p>
            <a 
              href="#contact" 
              className="mt-2 bg-navy-900 text-white font-medium py-2 px-4 rounded-lg inline-block w-fit hover:bg-navy-800 transition-colors"
            >
              Request a Call
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhySection;